import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Download, History, Printer } from 'lucide-react';

import {
  downloadAuditBinder,
  listSops,
  formatSopIdentifier,
  type AuditBinderScope,
  type SopListItem,
  type SopVersionState,
  type ServiceLineTag,
} from '@/api/sops.api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';

type ServiceLineFilter = 'all' | ServiceLineTag | 'untagged';

/**
 * Master SOP index — the page we hand the FDA inspector during the audit
 * walkthrough. Optimized for two consumption modes:
 *
 *  1. **On-screen.** Compact table grouped by family. Filter to KWF (audit
 *     scope) by default so the inspector only sees in-scope documents.
 *     Click any row to jump to the read-only detail view.
 *
 *  2. **On paper.** Print-friendly: chrome is hidden by AppShell's
 *     `print:hidden`, each family starts on a fresh page (`.print-page-break`),
 *     hyperlinks reveal their URLs so the printed copy is self-describing
 *     for an inspector who wants to verify the digital trail.
 *
 * Auditor-readonly seats land here straight from /compliance/audit-index.
 * Internal users reach it via the "Open master index" link on the SOP list.
 */
export function SopAuditIndexPage() {
  const { state: authState } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['compliance-sops'],
    queryFn: listSops,
  });

  // Default to KWF — that's the FDA audit scope. Any non-KWF SOP is
  // out-of-scope for the 2026-05 audit. We let the user broaden it
  // (e.g. for an internal review) but the default protects against
  // accidentally handing the inspector a KWPL document.
  const [serviceLineFilter, setServiceLineFilter] = useState<ServiceLineFilter>('kwf');

  // Binder download — kept simple (no react-query mutation) since the
  // server side already writes the audit-log row. We just toggle a local
  // "downloading" state to disable the button + surface a coarse error.
  const [binderDownloading, setBinderDownloading] = useState(false);
  const [binderError, setBinderError] = useState<string | null>(null);

  async function handleDownloadBinder(): Promise<void> {
    setBinderDownloading(true);
    setBinderError(null);
    try {
      // The binder endpoint only accepts kwf | kwpl | all — skip the
      // 'untagged' filter (we never offer an "untagged-only binder").
      const scope: AuditBinderScope = serviceLineFilter === 'untagged' ? 'all' : serviceLineFilter;
      await downloadAuditBinder({ scope });
    } catch (err) {
      setBinderError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setBinderDownloading(false);
    }
  }

  const filtered = useMemo<SopListItem[]>(() => {
    if (!data) return [];
    return data.filter((sop) => {
      if (serviceLineFilter === 'all') return true;
      if (serviceLineFilter === 'untagged') return sop.serviceLineTags.length === 0;
      return sop.serviceLineTags.includes(serviceLineFilter);
    });
  }, [data, serviceLineFilter]);

  // Group filtered SOPs by family in the canonical family order.
  const families = useMemo(() => {
    const order: SopListItem['family'][] = ['ADM', 'ISO', 'PAC', 'QUA', 'REC', 'SSOP', 'KQM'];
    const byFamily = new Map<string, SopListItem[]>();
    for (const sop of filtered) {
      const list = byFamily.get(sop.family) ?? [];
      list.push(sop);
      byFamily.set(sop.family, list);
    }
    // Sort each family by number so QUA-0001 comes before QUA-0002.
    for (const list of byFamily.values()) {
      list.sort((a, b) => a.number - b.number);
    }
    return order.filter((f) => byFamily.has(f)).map((f) => ({ family: f, sops: byFamily.get(f) ?? [] }));
  }, [filtered]);

  const generatedAt = useMemo(() => new Date(), []);
  const orgName = authState.status === 'authenticated' ? authState.me.organization.name : 'Kaerwell';

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div>
          <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load SOPs</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </Card>
    );
  }

  const totalSops = data?.length ?? 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6 print:max-w-none">
      {/* Toolbar — hidden on print so the printout starts with the cover header. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <Link
          to="/compliance/sops"
          className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
        >
          <ArrowLeft className="size-4" />
          Back to all SOPs
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/compliance/audit-log"
            className="inline-flex items-center gap-1.5 rounded-kw-md border border-kw-border bg-white px-3 py-1.5 text-[13px] font-medium text-kw-shark shadow-kw-xs hover:border-kw-cerulean/40 hover:text-kw-cerulean"
          >
            <History className="size-4" />
            Audit log
          </Link>
          <FilterSelect
            label="Service line"
            value={serviceLineFilter}
            onChange={(v) => {
              setServiceLineFilter(v as ServiceLineFilter);
            }}
            options={[
              { value: 'kwf', label: 'KWF (audit scope) — default' },
              { value: 'kwpl', label: 'KWPL only' },
              { value: 'untagged', label: 'Untagged' },
              { value: 'all', label: 'All service lines' },
            ]}
          />
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              window.print();
            }}
          >
            <Printer className="size-4" />
            Print index
          </Button>
          <Button
            size="sm"
            onClick={() => {
              void handleDownloadBinder();
            }}
            isLoading={binderDownloading}
          >
            <Download className="size-4" />
            Download binder
          </Button>
        </div>
      </div>

      {binderError ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3 text-[12px] text-kw-danger-text print:hidden"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>Could not generate audit binder: {binderError}</span>
        </div>
      ) : null}

      {/* Cover / header — always rendered (used both on-screen and as the
          first page of the printout). */}
      <header className="space-y-2 border-b-2 border-kw-cerulean-dark pb-4 print:border-black">
        <p className="font-mono text-[11px] uppercase tracking-wide text-kw-muted print:text-black">
          Quality Management System
        </p>
        <h1 className="typo-h1 print:text-[24px]">{orgName} — SOP Master Index</h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] text-kw-muted print:text-black">
          <span>
            Generated <strong>{generatedAt.toLocaleString()}</strong>
          </span>
          <span>
            Scope: <ServiceLineLabel filter={serviceLineFilter} />
          </span>
          <span>
            <strong>{filtered.length}</strong> of {totalSops} SOPs shown
          </span>
        </div>
        <p className="hidden text-[11px] text-kw-muted print:block">
          This index is generated from the live Quality Management System. Each SOP listed below has a current
          revision in the system; the on-screen version is the authoritative copy.
        </p>
      </header>

      {filtered.length === 0 ? (
        <Card className="p-8 text-center text-[13px] text-kw-muted">No SOPs match the current filter.</Card>
      ) : (
        families.map(({ family, sops }, familyIndex) => (
          <section
            key={family}
            aria-labelledby={`audit-family-${family}`}
            className={familyIndex > 0 ? 'print-page-break space-y-3' : 'space-y-3'}
          >
            <h2
              id={`audit-family-${family}`}
              className="typo-h2 border-b border-kw-border pb-1 print:border-black"
            >
              {familyLabel(family)} <span className="text-kw-muted">({sops.length})</span>
            </h2>
            <div className="overflow-hidden rounded-kw-md border border-kw-border print:rounded-none print:border-black">
              <table className="w-full text-[13px]">
                <thead className="bg-kw-bg text-left text-[11px] uppercase tracking-wide text-kw-muted print:bg-white print:text-black">
                  <tr>
                    <th className="w-[140px] px-3 py-2">Identifier</th>
                    <th className="px-3 py-2">Title</th>
                    <th className="w-[110px] px-3 py-2">State</th>
                    <th className="w-[110px] px-3 py-2">Service lines</th>
                    <th className="w-[110px] px-3 py-2">Published</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kw-border print:divide-black">
                  {sops.map((sop) => (
                    <tr key={sop.id} className="bg-white align-top hover:bg-kw-bg-blue print:hover:bg-white">
                      <td className="px-3 py-2 font-mono text-[12px] text-kw-shark">
                        <Link
                          to={`/compliance/sops/${sop.slug}`}
                          data-print-url
                          className="text-kw-cerulean hover:underline print:text-black"
                        >
                          {formatSopIdentifier({
                            family: sop.family,
                            number: sop.number,
                            revision: sop.latestVersion?.revision,
                          })}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-kw-shark">{sop.title}</td>
                      <td className="px-3 py-2">
                        {sop.latestVersion ? (
                          <StateBadge state={sop.latestVersion.state} />
                        ) : (
                          <span className="text-[11px] text-kw-muted">No version</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <ServiceLineCell tags={sop.serviceLineTags} />
                      </td>
                      <td className="px-3 py-2 text-[12px] text-kw-muted print:text-black">
                        {sop.latestVersion?.publishedAt
                          ? new Date(sop.latestVersion.publishedAt).toLocaleDateString()
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}

      {/* Footer — appears at the end of the document on screen and on
          the final printed page. Closes the document cleanly so the
          inspector knows nothing was truncated. */}
      <footer className="border-t border-kw-border pt-4 text-[11px] text-kw-muted print:border-black print:text-black">
        <p>
          End of index. {String(filtered.length)} SOP{filtered.length === 1 ? '' : 's'} listed. Generated by
          Kaerwell Staff Portal — Compliance Module.
        </p>
      </footer>
    </div>
  );
}

interface FilterOption {
  value: string;
  label: string;
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: FilterOption[];
}) {
  const id = `audit-index-filter-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-[12px] font-medium text-kw-muted">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ServiceLineLabel({ filter }: { filter: ServiceLineFilter }) {
  switch (filter) {
    case 'kwf':
      return <strong>KWF (Fulfillment, FDA audit scope)</strong>;
    case 'kwpl':
      return <strong>KWPL (Private Label)</strong>;
    case 'untagged':
      return <strong>Untagged SOPs</strong>;
    default:
      return <strong>All service lines</strong>;
  }
}

function ServiceLineCell({ tags }: { tags: ServiceLineTag[] }) {
  if (tags.length === 0) {
    return (
      <span className="text-[11px] uppercase tracking-wide text-kw-ghost print:text-black">Untagged</span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <span
          key={tag}
          className={
            tag === 'kwf'
              ? 'rounded-kw-full bg-kw-cerulean-light px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-kw-cerulean-dark print:bg-white print:text-black print:ring-1 print:ring-black'
              : 'rounded-kw-full bg-kw-emerald-light px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-kw-tag-text print:bg-white print:text-black print:ring-1 print:ring-black'
          }
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

function familyLabel(family: string): string {
  switch (family) {
    case 'ADM':
      return 'ADM — Administrative';
    case 'ISO':
      return 'ISO — ISO-Aligned Procedures';
    case 'PAC':
      return 'PAC — Packaging';
    case 'QUA':
      return 'QUA — Quality';
    case 'REC':
      return 'REC — Receiving / Shipping';
    case 'SSOP':
      return 'SSOP — Sanitation';
    case 'KQM':
      return 'KQM — Quality Manual';
    default:
      return family;
  }
}

function StateBadge({ state }: { state: SopVersionState }) {
  switch (state) {
    case 'approved':
      return <Badge variant="success">Approved</Badge>;
    case 'pending_approval':
      return <Badge variant="warning">Pending</Badge>;
    case 'superseded':
      return <Badge variant="neutral">Superseded</Badge>;
    case 'draft':
    default:
      return <Badge variant="info">Draft</Badge>;
  }
}
